import { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { collection, deleteDoc, doc, getDocs, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { CircleFlag } from "react-circle-flags";
import { countryMatchesSearch } from "../../utils/iso2to3";
import Modal from "../ui/Modal";
import { useLockBodyScroll } from "../../kyc/sections/common/common_utils/useLockBodyScroll";
import { appConfirm } from "../ui/appConfirm";
import {
  CORPORATE_DOCUMENT_REQUIREMENT_COLLECTION,
  CORPORATE_DOC_TYPES,
  CORPORATE_CASE_LOGICS,
  CORPORATE_DOC_CONTENT_FIELDS,
  CORPORATE_DOC_EQUIVALENT_FIELDS,
  CORPORATE_DETAIL_VALUES,
  corporateDocDetails,
  corporateRequirementCases,
  corporateRequirementsCsv,
} from "../../utils/corporateDocumentRequirements";
import CorporateDocumentTypesModal from "./CorporateDocumentTypesModal";
import "./css/CorporateDocumentRequirementsEditor.css";



const TEXT_FIELDS = [
  { key: "issuer", label: "Issuer" },
  { key: "legal_system", label: "Legal system" },
  { key: "available_online", label: "Available online" },
  { key: "free_access", label: "Free access" },
  { key: "official_url", label: "Official URL" },
  { key: "online_notes", label: "Online notes" },
];

// Legal systems offered in the editor ("Other" allows a custom one).
const LEGAL_SYSTEMS = [
  "Civil law",
  "Common law",
  "Mixed (civil law + common law)",
  "Mixed (civil law + Sharia)",
  "Mixed (civil law + common law + Sharia)",
  "Sharia law",
  "Customary law",
];
// Former spreadsheet wordings, shown as the closest option (stored as such when the country is saved).
const LEGAL_SYSTEM_ALIASES = {
  "Common law (based on Ottoman, British Mandate, and Israeli law)": "Mixed (civil law + common law)",
  "Mixed (common law influenced)": "Mixed (civil law + common law)",
  "Mixed (common law + civil law influence)": "Mixed (civil law + common law)",
  "Mixed (civil law + common law influence)": "Mixed (civil law + common law)",
  "Civil law (Canon law)": "Civil law",
};
const CUSTOM_LEGAL_SYSTEM = "__custom__";

const emptyDoc = () => ({
  local: "",
  english: "",
  type: "registration",
  optional: false,
  contains: corporateDocDetails({}, CORPORATE_DOC_CONTENT_FIELDS),
  equivalent: corporateDocDetails({}, CORPORATE_DOC_EQUIVALENT_FIELDS),
  details_note: "",
});

const detailValue = (value) => CORPORATE_DETAIL_VALUES.find((v) => v.value === value) || CORPORATE_DETAIL_VALUES[3];

// Ten small indicators per document: what it contains, and what it is equivalent to.
function DocDetailChips({ doc }) {
  const chip = (group, { key, label, short }, value) => {
    const v = detailValue(value);
    return (
      <span key={`${group}-${key}`} className={`corpReqChip corpReqChip--${v.value}`}
        title={`${group === "c" ? "Contains" : "Equivalent to"} ${label}: ${v.label}${doc.details_note ? ` — ${doc.details_note}` : ""}`}>
        {v.symbol} {short}
      </span>
    );
  };
  return (
    <div className="corpReqChips">
      {CORPORATE_DOC_CONTENT_FIELDS.map((f) => chip("c", f, doc.contains?.[f.key]))}
      <span className="corpReqChipSep" aria-hidden="true">≈</span>
      {CORPORATE_DOC_EQUIVALENT_FIELDS.map((f) => chip("e", f, doc.equivalent?.[f.key]))}
    </div>
  );
}
const emptyCase = () => ({ name: "", logic: "ALL", docs: [emptyDoc()] });

function typeLabel(type) {
  return CORPORATE_DOC_TYPES.find((t) => t.value === type)?.label || type;
}

function logicLabel(logic) {
  return CORPORATE_CASE_LOGICS.find((l) => l.value === logic)?.label || logic;
}

function docText(item) {
  const local = String(item?.local || "").trim();
  const english = String(item?.english || "").trim();
  if (local && english && local.toLowerCase() !== english.toLowerCase()) return `${local} — ${english}`;
  return local || english;
}

// Editable copy of a stored record.
function draftFromRecord(row) {
  return {
    isNew: !row,
    iso2: row?.iso2 || row?.id || "",
    country: row?.country || "",
    cases: row ? corporateRequirementCases(row).map((c) => ({ ...c, docs: c.docs.map((d) => ({ ...d })) })) : [emptyCase()],
    guidance: row?.guidance || "",
    notes: row?.notes || "",
    ...Object.fromEntries(TEXT_FIELDS.map(({ key }) => [key, row?.[key] || ""])),
    legal_system: LEGAL_SYSTEM_ALIASES[row?.legal_system] || row?.legal_system || "",
  };
}

function validateDraft(draft, rows) {
  const iso2 = draft.iso2.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(iso2)) return "ISO 2 must be two letters.";
  if (draft.isNew && rows.some((r) => (r.iso2 || r.id) === iso2)) return `${iso2} already exists.`;
  if (!draft.country.trim()) return "Country is required.";
  const cases = draft.cases.map((c) => ({ ...c, docs: c.docs.filter((d) => d.local.trim() || d.english.trim()) }));
  if (!cases.length || cases.some((c) => !c.docs.length)) return "Each case needs at least one document.";
  if (cases.some((c) => c.docs.every((d) => d.optional))) return "Each case needs at least one required (not optional) document.";
  if (cases.length > 1 && cases.some((c) => !c.name.trim())) return "Name each case when there are several.";
  return "";
}

// Admin sources card: the list of countries, maintained here (add / edit / delete, CSV export).
export default function CorporateDocumentRequirementsEditor() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const toastRef = useRef(null);
  const notify = (detail) => toastRef.current?.show({ severity: "success", summary: "Saved", detail, life: 2500 });
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState(null);
  const [typesInfoOpen, setTypesInfoOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function loadRows() {
    setError("");
    try {
      const snap = await getDocs(collection(db, CORPORATE_DOCUMENT_REQUIREMENT_COLLECTION));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => String(a.country || a.iso2 || a.id).localeCompare(String(b.country || b.iso2 || b.id)));
      setRows(list);
    } catch (e) {
      setError(e?.message || "The list could not be loaded.");
    }
  }

  useEffect(() => {
    loadRows();
  }, []);

  // Page behind the edit modal stays fixed.
  useLockBodyScroll(!!draft);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!rows || !q) return rows || [];
    return rows.filter((row) => countryMatchesSearch({ value: row.iso2 || row.id, label: row.country }, q) || [
      row.iso2, row.id, row.country, row.guidance, row.issuer,
      ...corporateRequirementCases(row).flatMap((c) => [c.name, ...c.docs.flatMap((d) => [d.local, d.english])]),
    ].some((value) => String(value || "").toLowerCase().includes(q)));
  }, [rows, search]);

  function exportCsv() {
    // BOM so Excel opens the file as UTF-8 (local names in Arabic, German…).
    const blob = new Blob(["﻿" + corporateRequirementsCsv(rows || [])], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `corporate-document-requirements-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function saveDraft() {
    const problem = validateDraft(draft, rows || []);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const iso2 = draft.iso2.trim().toUpperCase();
      const clean = (value) => String(value || "").trim();
      // Full replace: also clears the "Review" flag set on the drafted cases.
      await setDoc(doc(db, CORPORATE_DOCUMENT_REQUIREMENT_COLLECTION, iso2), {
        iso2,
        country: clean(draft.country),
        cases: draft.cases.map((c) => ({
          name: clean(c.name),
          logic: c.logic,
          docs: c.docs
            .map((d) => ({
              local: clean(d.local),
              english: clean(d.english),
              type: d.type,
              optional: d.optional === true,
              contains: corporateDocDetails(d.contains, CORPORATE_DOC_CONTENT_FIELDS),
              equivalent: corporateDocDetails(d.equivalent, CORPORATE_DOC_EQUIVALENT_FIELDS),
              details_note: clean(d.details_note),
            }))
            .filter((d) => d.local || d.english),
        })),
        guidance: clean(draft.guidance),
        notes: clean(draft.notes),
        ...Object.fromEntries(TEXT_FIELDS.map(({ key }) => [key, clean(draft[key])])),
        format: 2,
        updatedAt: serverTimestamp(),
        updatedByEmail: auth.currentUser?.email || null,
      });
      notify(`${draft.country.trim()} saved.`);
      setDraft(null);
      await loadRows();
    } catch (e) {
      setError(e?.message || "The country could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  function confirmDelete(row) {
    appConfirm({
      group: "appConfirm",
      header: "Delete country",
      message: `Delete the corporate document requirements for ${row.country || row.id}? KYC files for companies from this country will show the generic register extract instead.`,
      defaultFocus: "reject",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      scheme: "danger",
      accept: async () => {
        try {
          await deleteDoc(doc(db, CORPORATE_DOCUMENT_REQUIREMENT_COLLECTION, row.id));
          notify(`${row.country || row.id} deleted.`);
          await loadRows();
        } catch (e) {
          setError(e?.message || "The country could not be deleted.");
        }
      },
    });
  }

  // Draft edits
  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const setCase = (ci, patch) => setDraft((d) => ({ ...d, cases: d.cases.map((c, i) => (i === ci ? { ...c, ...patch } : c)) }));
  const [openDetails, setOpenDetails] = useState({});
  const setDocDetail = (ci, di, group, key, value) =>
    setDocField(ci, di, { [group]: { ...draft.cases[ci].docs[di][group], [key]: value } });
  const setDocField = (ci, di, patch) =>
    setCase(ci, { docs: draft.cases[ci].docs.map((dd, j) => (j === di ? { ...dd, ...patch } : dd)) });

  const legalSystemCustom = draft ? draft.legalSystemCustom || (!!draft.legal_system && !LEGAL_SYSTEMS.includes(draft.legal_system)) : false;

  // Yes / No / Partial, keeping any other stored value selectable.
  const availabilityOptions = (value) => ["", "Yes", "Partial", "No", ...(value && !["Yes", "Partial", "No"].includes(value) ? [value] : [])];

  const editor = draft ? (
    <div className="corpReqEditor">
      <section className="corpReqSection">
        <div className="corpReqCountryRow">
          <div className="corpReqFlag" aria-hidden="true">
            {/^[A-Z]{2}$/.test(draft.iso2) ? (
              <CircleFlag className="flagMutedImg" countryCode={draft.iso2.toLowerCase()} height="36" />
            ) : <i className="bi bi-globe2" />}
          </div>
          <input className="form-control form-control-sm" maxLength={2} value={draft.iso2} placeholder="ISO"
            aria-label="ISO code" disabled={!draft.isNew} onChange={(e) => setField("iso2", e.target.value.toUpperCase())} />
          <input className="form-control form-control-sm" value={draft.country} placeholder="Country name"
            aria-label="Country name" onChange={(e) => setField("country", e.target.value)} />
        </div>
      </section>

      <section className="corpReqSection">
        <div className="corpReqSectionHead">
          <div className="corpReqSectionTitle">Required documents</div>
          <div className="corpReqSectionHint">
            One case per type of company (e.g. mainland / free zone). The requirement is complete when any one case is complete.
          </div>
        </div>

        {draft.cases.map((kase, ci) => (
          <div key={ci}>
            {ci > 0 ? <div className="corpReqOr"><span>or</span></div> : null}
            <div className="corpReqCaseEdit">
              <div className="corpReqCaseHead">
                <span className="corpReqCaseNumber">Case {ci + 1}</span>
                <input className="form-control form-control-sm"
                  placeholder={draft.cases.length > 1 ? "Case name, e.g. Mainland company" : "Case name (optional when there is one case)"}
                  value={kase.name} onChange={(e) => setCase(ci, { name: e.target.value })} aria-label="Case name" />
                <div className="corpReqRule" role="radiogroup" aria-label="Rule">
                  {CORPORATE_CASE_LOGICS.map((l) => (
                    <button key={l.value} type="button" role="radio" aria-checked={kase.logic === l.value}
                      className={kase.logic === l.value ? "is-selected" : ""} onClick={() => setCase(ci, { logic: l.value })}>
                      {l.label}
                    </button>
                  ))}
                </div>
                {draft.cases.length > 1 ? (
                  <button type="button" className="btn btn-sm btn-link text-secondary corpReqIconBtn" aria-label={`Remove case ${ci + 1}`}
                    onClick={() => setDraft((d) => ({ ...d, cases: d.cases.filter((_, i) => i !== ci) }))}>
                    <i className="bi bi-trash" aria-hidden="true" />
                  </button>
                ) : null}
              </div>

              <div className="corpReqDocGrid corpReqDocGridHead" aria-hidden="true">
                <span>Local name</span><span>English name</span><span>Type</span><span>Optional</span><span /><span />
              </div>
              {kase.docs.map((d, di) => (
                <div key={di}>
                <div className="corpReqDocGrid">
                  <input className="form-control form-control-sm" placeholder="e.g. Handelsregisterauszug" value={d.local}
                    onChange={(e) => setDocField(ci, di, { local: e.target.value })} aria-label="Local name" />
                  <input className="form-control form-control-sm" placeholder="e.g. Commercial register extract" value={d.english}
                    onChange={(e) => setDocField(ci, di, { english: e.target.value })} aria-label="English name" />
                  <select className="form-select form-select-sm" value={d.type} onChange={(e) => setDocField(ci, di, { type: e.target.value })}
                    aria-label="Document type">
                    {CORPORATE_DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <label className="corpReqOptional" title="Listed but not required for the requirement to be complete">
                    <input type="checkbox" className="form-check-input" checked={d.optional === true}
                      onChange={(e) => setDocField(ci, di, { optional: e.target.checked })} aria-label="Optional" />
                  </label>
                  <button type="button" className="btn btn-sm btn-link text-secondary corpReqIconBtn"
                    aria-label="Document details" aria-expanded={!!openDetails[`${ci}-${di}`]} title="What the document contains / is equivalent to"
                    onClick={() => setOpenDetails((o) => ({ ...o, [`${ci}-${di}`]: !o[`${ci}-${di}`] }))}>
                    <i className={`bi ${openDetails[`${ci}-${di}`] ? "bi-chevron-up" : "bi-list-check"}`} aria-hidden="true" />
                  </button>
                  <button type="button" className="btn btn-sm btn-link text-secondary corpReqIconBtn" aria-label="Remove document"
                    disabled={kase.docs.length === 1}
                    onClick={() => setCase(ci, { docs: kase.docs.filter((_, j) => j !== di) })}>
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </div>
                {openDetails[`${ci}-${di}`] ? (
                  <div className="corpReqDetails">
                    {[["contains", "Contains", CORPORATE_DOC_CONTENT_FIELDS], ["equivalent", "Equivalent to", CORPORATE_DOC_EQUIVALENT_FIELDS]].map(([group, title, fields]) => (
                      <div key={group} className="corpReqDetailsGroup">
                        <div className="corpReqDetailsTitle">{title}</div>
                        <div className="corpReqDetailsGrid">
                          {fields.map(({ key, label }) => (
                            <label key={key} className="corpReqDetailsField">
                              <span>{label}</span>
                              <select className="form-select form-select-sm" value={d[group]?.[key] || "unknown"}
                                aria-label={`${title} ${label}`} onChange={(e) => setDocDetail(ci, di, group, key, e.target.value)}>
                                {CORPORATE_DETAIL_VALUES.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                              </select>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                    <input className="form-control form-control-sm" placeholder="Note (e.g. shareholders shown only for LLCs)"
                      aria-label="Details note" value={d.details_note || ""} onChange={(e) => setDocField(ci, di, { details_note: e.target.value })} />
                  </div>
                ) : null}
                </div>
              ))}
              <button type="button" className="btn btn-sm btn-link corpReqAddLink" onClick={() => setCase(ci, { docs: [...kase.docs, emptyDoc()] })}>
                <i className="bi bi-plus-lg me-1" aria-hidden="true" />Add document
              </button>
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-sm btn-outline-secondary"
          onClick={() => setDraft((d) => ({ ...d, cases: [...d.cases, emptyCase()] }))}>
          <i className="bi bi-plus-lg me-1" aria-hidden="true" />Add another case
        </button>
      </section>

      <section className="corpReqSection">
        <div className="corpReqSectionHead">
          <div className="corpReqSectionTitle">Guidance and notes</div>
        </div>
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <label className="form-label small" htmlFor="corpReqGuidance">Guidance for staff</label>
            <textarea id="corpReqGuidance" className="form-control form-control-sm" rows={3} value={draft.guidance}
              placeholder="Shown to staff with the requirement" onChange={(e) => setField("guidance", e.target.value)} />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label small" htmlFor="corpReqNotes">Internal notes</label>
            <textarea id="corpReqNotes" className="form-control form-control-sm" rows={3} value={draft.notes}
              onChange={(e) => setField("notes", e.target.value)} />
          </div>
        </div>
      </section>

      <section className="corpReqSection">
        <div className="corpReqSectionHead">
          <div className="corpReqSectionTitle">Source and access</div>
        </div>
        <div className="row g-3">
          <div className="col-12 col-md-8">
            <label className="form-label small" htmlFor="corpReq_issuer">Issuer</label>
            <input id="corpReq_issuer" className="form-control form-control-sm" value={draft.issuer}
              onChange={(e) => setField("issuer", e.target.value)} />
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small" htmlFor="corpReq_legal_system">Legal system</label>
            <select id="corpReq_legal_system" className="form-select form-select-sm"
              value={legalSystemCustom ? CUSTOM_LEGAL_SYSTEM : draft.legal_system}
              onChange={(e) => {
                const value = e.target.value;
                setDraft((d) => value === CUSTOM_LEGAL_SYSTEM
                  ? { ...d, legalSystemCustom: true, legal_system: "" }
                  : { ...d, legalSystemCustom: false, legal_system: value });
              }}>
              <option value="">—</option>
              {LEGAL_SYSTEMS.map((v) => <option key={v} value={v}>{v}</option>)}
              <option value={CUSTOM_LEGAL_SYSTEM}>Other (enter a custom system)…</option>
            </select>
            {legalSystemCustom ? (
              <input className="form-control form-control-sm mt-2" placeholder="Custom legal system" aria-label="Custom legal system"
                value={draft.legal_system} onChange={(e) => setField("legal_system", e.target.value)} autoFocus />
            ) : null}
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label small" htmlFor="corpReq_official_url">Official website</label>
            <input id="corpReq_official_url" className="form-control form-control-sm" type="url" placeholder="https://"
              value={draft.official_url} onChange={(e) => setField("official_url", e.target.value)} />
          </div>
          {[["available_online", "Available online"], ["free_access", "Free access"]].map(([key, label]) => (
            <div key={key} className="col-6 col-md-3">
              <label className="form-label small" htmlFor={`corpReq_${key}`}>{label}</label>
              <select id={`corpReq_${key}`} className="form-select form-select-sm" value={draft[key]}
                onChange={(e) => setField(key, e.target.value)}>
                {availabilityOptions(draft[key]).map((v) => <option key={v} value={v}>{v || "—"}</option>)}
              </select>
            </div>
          ))}
          <div className="col-12">
            <label className="form-label small" htmlFor="corpReq_online_notes">Online access notes</label>
            <textarea id="corpReq_online_notes" className="form-control form-control-sm" rows={2} value={draft.online_notes}
              onChange={(e) => setField("online_notes", e.target.value)} />
          </div>
        </div>
      </section>
    </div>
  ) : null;

  const footer = (
    <div className="d-flex justify-content-end gap-2 w-100">
      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => { setDraft(null); setError(""); }}>
        Cancel
      </button>
      <button type="button" className="btn btn-sm btn-activation" disabled={busy} onClick={saveDraft}>
        {busy ? "Saving…" : "Save"}
      </button>
    </div>
  );

  const lastUpdated = (rows || [])
    .map((row) => row.updatedAt?.toDate?.())
    .filter(Boolean)
    .sort((a, b) => b - a)[0];

  return (
    <section className="card shadow-sm mb-3" aria-labelledby="corp-req-title">
      <Toast ref={toastRef} />
      <div className="p-3 border-bottom d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <div>
          <div id="corp-req-title" className="fw-bold d-flex align-items-center gap-1" style={{ color: "#4C5465" }}>
            Corporate document requirements
            <button type="button" className="corpReqInfoBtn" aria-label="About document types" title="About document types"
              onClick={() => setTypesInfoOpen(true)}>
              <i className="bi bi-info-circle" aria-hidden="true" />
            </button>
          </div>
          <div className="small text-muted">
            Corporate documents required per country. List every case (e.g. mainland / free zone company): the
            requirement is complete when any one case is complete.
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!rows?.length} onClick={exportCsv}>
            <i className="bi bi-download me-1" aria-hidden="true" /> Export CSV
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!rows}
            onClick={() => { setDraft(draftFromRecord(null)); setError(""); }}>
            <i className="bi bi-plus-lg me-1" aria-hidden="true" /> Add country
          </button>
        </div>
      </div>

      <div className="p-3">
        {error && !draft ? <div className="alert alert-danger py-2" role="alert">{error}</div> : null}
        {!rows && !error ? <div className="text-muted small" role="status">Loading countries…</div> : null}

        {rows ? (
          <>
            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6">
                <label className="form-label small" htmlFor="corp-req-filter">Filter the table</label>
                <input id="corp-req-filter" className="form-control" type="search"
                  placeholder="Country, ISO code, case or document" value={search}
                  onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>

            <div className="corpReqLegend">
              <span><b>Under each document:</b> contains {CORPORATE_DOC_CONTENT_FIELDS.map((f) => f.short).join(", ")}</span>
              <span>≈ equivalent to {CORPORATE_DOC_EQUIVALENT_FIELDS.map((f) => f.short).join(", ")}</span>
              <span>
                {CORPORATE_DETAIL_VALUES.map((v) => (
                  <span key={v.value} className={`corpReqChip corpReqChip--${v.value}`}>{v.symbol} {v.label}</span>
                ))}
              </span>
            </div>

            <div className="corpReqTableWrap">
              <table className="table table-sm align-top m-0 corpReqTable">
                <thead>
                  <tr>
                    <th>ISO</th>
                    <th>Country</th>
                    <th>Cases and documents</th>
                    <th>Guidance / notes</th>
                    <th>Issuer</th>
                    <th>Online</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => {
                    const cases = corporateRequirementCases(row);
                    const guidance = row.guidance;
                    return (
                      <tr key={row.id}>
                        <td className="corpReqIso">{row.iso2 || row.id}</td>
                        <td className="corpReqCountry">
                          {row.country || "—"}
                          {row.review ? <div><span className="corpReqFlag corpReqFlag--review" title={row.review}>Review</span></div> : null}
                        </td>
                        <td className="corpReqCases">
                          {cases.map((c, ci) => (
                            <div key={ci} className="corpReqCase">
                              <div className="corpReqCaseTitle">
                                {c.name ? <span>{c.name}</span> : null}
                                <span className={`corpReqLogic corpReqLogic--${c.logic.toLowerCase()}`}>{logicLabel(c.logic)}</span>
                              </div>
                              <ul className="corpReqDocs">
                                {c.docs.map((d, di) => (
                                  <li key={di} className={d.optional ? "corpReqDocOptional" : ""}>
                                    {docText(d)} <span className="corpReqType">{typeLabel(d.type)}</span>
                                    {d.optional ? <span className="corpReqType">Optional</span> : null}
                                    <DocDetailChips doc={d} />
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </td>
                        <td className="corpReqCondition">
                          {guidance ? <div>{guidance}</div> : null}
                          {row.notes ? <div className="text-muted">{row.notes}</div> : null}
                          {!guidance && !row.notes ? "—" : null}
                        </td>
                        <td className="corpReqIssuer">{row.issuer || "—"}</td>
                        <td className="corpReqOnline">
                          {row.available_online ? <div>Available: {row.available_online}</div> : null}
                          {row.free_access ? <div>Free: {row.free_access}</div> : null}
                          {row.official_url ? (
                            <a href={row.official_url} target="_blank" rel="noopener noreferrer">Official site</a>
                          ) : null}
                          {!row.available_online && !row.free_access && !row.official_url ? "—" : null}
                        </td>
                        <td className="corpReqActions">
                          <button type="button" className="btn btn-sm btn-outline-secondary" aria-label={`Edit ${row.country || row.id}`}
                            onClick={() => { setDraft(draftFromRecord(row)); setError(""); }}>
                            Edit
                          </button>
                          <button type="button" className="btn btn-sm btn-link text-secondary ms-1" aria-label={`Delete ${row.country || row.id}`}
                            onClick={() => confirmDelete(row)}>
                            <i className="bi bi-trash" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!filtered.length ? (
                    <tr>
                      <td colSpan="7" className="text-muted text-center p-3">
                        {rows.length ? "No countries match your search." : "No countries in the list."}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="small text-muted mt-3">
              {search.trim() ? `${filtered.length} of ${rows.length} countries` : `${rows.length} countries`}
              <br />
              Last updated: {lastUpdated ? lastUpdated.toLocaleString() : "—"}
            </div>
          </>
        ) : null}
      </div>

      <Modal open={!!draft} onClose={() => { if (!busy) { setDraft(null); setError(""); } }} footer={footer} className="corpReqModal"
        title={draft?.isNew ? "Add country" : `Edit ${draft?.country || draft?.iso2 || ""}`}>
        {error ? <div className="alert alert-danger py-2 small m-0">{error}</div> : null}
        {editor}
      </Modal>

      <CorporateDocumentTypesModal open={typesInfoOpen} onClose={() => setTypesInfoOpen(false)} />
    </section>
  );
}
